import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { I18nextProvider } from 'react-i18next';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import i18n from '@app/i18n';
import { createHelixTheme } from '@design-system/theme/createHelixTheme';
import { useUiStore } from '@shared/store/uiStore';
import { getCurrentUser } from '@features/auth/api/auth';
import { useAuthStore } from '@features/auth/store/authStore';
import { shouldRetryApiQuery } from '@shared/api/rateLimitCooldown';

const ltrCache = createCache({ key: 'helix-ltr' });
const rtlCache = createCache({ key: 'helix-rtl', stylisPlugins: [prefixer, rtlPlugin] });

export function AppProviders({ children }: PropsWithChildren) {
  const mode = useUiStore((state) => state.mode);
  const locale = useUiStore((state) => state.locale);
  const direction = locale === 'ar' ? 'rtl' : 'ltr';
  const authStatus = useAuthStore((state) => state.status);
  const completeLogin = useAuthStore((state) => state.completeLogin);
  const logout = useAuthStore((state) => state.logout);
  const theme = useMemo(() => createHelixTheme(mode, direction), [direction, mode]);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: shouldRetryApiQuery, refetchOnWindowFocus: false },
          mutations: { retry: 0 },
        },
      }),
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    void i18n.changeLanguage(locale);
  }, [direction, locale]);

  useEffect(() => {
    if (authStatus !== 'loading') return;
    let active = true;
    void getCurrentUser()
      .then((user) => {
        if (active) completeLogin(user);
      })
      .catch(() => {
        if (active) logout();
      });
    return () => {
      active = false;
    };
  }, [authStatus, completeLogin, logout]);

  return (
    <I18nextProvider i18n={i18n}>
      <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </ThemeProvider>
      </CacheProvider>
    </I18nextProvider>
  );
}
